import React, { useEffect, useMemo, useState } from 'react';
import { FiTrash2, FiX, FiUsers, FiMail, FiShield, FiEdit2 } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import { Avatar } from '../ui/Avatar';
import { extractApiError } from '../../utils/apiError';
import {
  useGetWorkspaces,
  useGetWorkspaceMembers,
  useGetWorkspaceInvitations,
  useInviteWorkspaceMember,
  // useResendWorkspaceInvitation,
  useCancelWorkspaceInvitation,
  useUpdateWorkspaceMemberRole,
  useRemoveWorkspaceMember,
  useUpdateWorkspace,
  type AssignableWorkspaceRole,
  type WorkspaceMember,
  type WorkspaceInvitation,
} from '../../api/workspaces';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  manager: 'Manager',
  editor: 'Editor',
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const displayName = (u: { full_name: string | null; username: string | null; email: string }) =>
  u.full_name?.trim() || u.username?.trim() || u.email;

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
  isDestructive?: boolean;
  onConfirm: () => void;
}

interface CollaboratorsAccessCardProps {
  workspaceUuid: string | undefined;
  currentUserId?: string | number;
  /** Set when embedding inside another container (e.g. a modal) that already renders its own heading/chrome. */
  embedded?: boolean;
  isArchived?: boolean;
}

export const CollaboratorsAccessCard: React.FC<CollaboratorsAccessCardProps> = ({
  workspaceUuid,
  currentUserId,
  embedded = false,
  isArchived = false,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AssignableWorkspaceRole>('editor');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const workspacesQuery = useGetWorkspaces();
  const membersQuery = useGetWorkspaceMembers(workspaceUuid);
  const invitationsQuery = useGetWorkspaceInvitations(workspaceUuid, { pending: true });

  const inviteMutation = useInviteWorkspaceMember(workspaceUuid);
  // const resendMutation = useResendWorkspaceInvitation(workspaceUuid);
  const cancelMutation = useCancelWorkspaceInvitation(workspaceUuid);
  const updateRoleMutation = useUpdateWorkspaceMemberRole(workspaceUuid);
  const removeMutation = useRemoveWorkspaceMember(workspaceUuid);
  const updateWorkspaceMutation = useUpdateWorkspace();

  const workspaceInfo = useMemo(
    () => workspacesQuery.data?.results.find((w) => w.uuid === workspaceUuid),
    [workspacesQuery.data, workspaceUuid],
  );
  const myRole = workspaceInfo?.role;
  const canManage = !isArchived && (myRole === 'owner' || myRole === 'manager');

  // Close the rename field if the workspace changes underneath us.
  useEffect(() => {
    setIsEditingName(false);
  }, [workspaceUuid]);

  const startEditingName = () => {
    setNameDraft(workspaceInfo?.name ?? '');
    setIsEditingName(true);
  };

  const handleSaveName = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = nameDraft.trim();
    if (!workspaceUuid || !trimmed || trimmed === workspaceInfo?.name) {
      setIsEditingName(false);
      return;
    }
    updateWorkspaceMutation.mutate(
      { workspaceUuid, name: trimmed },
      { onSuccess: () => setIsEditingName(false) },
    );
  };

  const members = membersQuery.data?.results ?? [];
  const memberCount = membersQuery.data?.count ?? members.length;
  const pendingInvitations = invitationsQuery.data?.results ?? [];

  const isSelf = (m: WorkspaceMember) => String(m.user.id) === String(currentUserId);

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();

    if (!trimmed) {
      setEmailError('Email is required.');
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (members.some((m) => m.user.email.toLowerCase() === trimmed)) {
      setEmailError('This user is already a member of this workspace.');
      return;
    }
    if (pendingInvitations.some((inv) => inv.email.toLowerCase() === trimmed)) {
      setEmailError('An invitation is already pending for this email.');
      return;
    }
    setEmailError(undefined);

    inviteMutation.mutate(
      { email: trimmed, role },
      {
        onSuccess: () => {
          setEmail('');
          setRole('editor');
        },
      },
    );
  };

  const requestRemoveMember = (member: WorkspaceMember) => {
    const self = isSelf(member);
    setConfirm({
      title: self ? 'Leave workspace?' : 'Remove member?',
      message: self
        ? `You will lose access to this workspace and its projects. This cannot be undone.`
        : `${displayName(member.user)} will lose access to this workspace. This cannot be undone.`,
      confirmLabel: self ? 'Leave' : 'Remove',
      onConfirm: () => {
        removeMutation.mutate(member.uuid);
        setConfirm(null);
      },
    });
  };

  const requestCancelInvitation = (invitation: WorkspaceInvitation) => {
    setConfirm({
      title: 'Cancel invitation?',
      message: `The invitation sent to ${invitation.email} will be revoked.`,
      confirmLabel: 'Cancel Invitation',
      onConfirm: () => {
        cancelMutation.mutate(invitation.uuid);
        setConfirm(null);
      },
    });
  };

  // const requestResendInvitation = (invitation: WorkspaceInvitation) => {
  //   setConfirm({
  //     title: 'Resend invitation?',
  //     message: `An invitation email will be re-sent to ${invitation.email}.`,
  //     confirmLabel: 'Resend',
  //     isDestructive: false,
  //     onConfirm: () => {
  //       resendMutation.mutate(invitation.uuid);
  //       setConfirm(null);
  //     },
  //   });
  // };

  const membersError = membersQuery.isError
    ? extractApiError(membersQuery.error, 'Failed to load members')
    : null;
  const invitationsError = invitationsQuery.isError
    ? extractApiError(invitationsQuery.error, 'Failed to load invitations')
    : null;

  return (
    <div
      className={
        embedded
          ? 'space-y-5'
          : 'bg-[#0B0B0B] border border-white/10 rounded-[28px] p-4 sm:p-6 space-y-5'
      }
    >
      {!embedded && (
        <div>
          <h3 className="text-base font-bold text-white">Collaborators & Access</h3>
          <p className="text-zinc-550 text-xs mt-1">
            Manage who has access to this project's workspace and their roles.
          </p>
        </div>
      )}

      {/* Access summary */}
      <div className="flex flex-wrap items-center gap-2.5 p-3.5 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl">
        {isEditingName ? (
          <form onSubmit={handleSaveName} className="flex items-center gap-1.5">
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              disabled={updateWorkspaceMutation.isPending}
              className="bg-[#0F0F0F] border border-blue-500/40 outline-none text-white text-[10px] font-bold uppercase tracking-wider rounded-lg px-2 py-1 w-40"
            />
            <button
              type="submit"
              onMouseDown={(e) => e.preventDefault()}
              disabled={updateWorkspaceMutation.isPending}
              className="text-[10px] font-bold text-blue-400 hover:text-blue-300 cursor-pointer disabled:opacity-40"
            >
              Save
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-1.5 text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
            <FiShield className="w-3.5 h-3.5" />
            <span>{workspaceInfo?.name ?? 'Workspace'}</span>
            {myRole === 'owner' && workspaceInfo && (
              <button
                type="button"
                onClick={startEditingName}
                title="Rename workspace"
                className="p-1 rounded-full hover:bg-white/10 text-zinc-500 hover:text-white transition-colors cursor-pointer"
              >
                <FiEdit2 className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
        <span className="text-zinc-650">·</span>
        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border bg-blue-500/10 text-blue-400 border-blue-500/20">
          Your role: {myRole ? (ROLE_LABEL[myRole] ?? myRole) : '—'}
        </span>
        <span className="text-zinc-650">·</span>
        <span className="text-zinc-500 text-[10px] font-semibold">
          {memberCount} collaborator{memberCount === 1 ? '' : 's'}
        </span>
      </div>

      {/* Invite form */}
      {canManage ? (
        <form onSubmit={handleInvite} className="space-y-3">
          <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
            Invite a Collaborator
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-550 w-4 h-4" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(undefined);
                  }}
                  placeholder="collaborator@email.com"
                  className={`w-full bg-[#0F0F0F] border hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3.5 transition-all ${
                    emailError ? 'border-rose-500' : 'border-white/5'
                  }`}
                />
              </div>
              {emailError && (
                <p className="text-rose-500 text-[10px] font-semibold mt-1.5">{emailError}</p>
              )}
            </div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as AssignableWorkspaceRole)}
              className="bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all cursor-pointer"
            >
              <option value="editor">Editor</option>
              <option value="manager">Manager</option>
            </select>
            <button
              type="submit"
              disabled={inviteMutation.isPending}
              className="px-6 py-3.5 rounded-xl bg-white text-black hover:bg-zinc-100 text-xs font-bold transition-all cursor-pointer shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {inviteMutation.isPending ? 'Inviting...' : 'Invite'}
            </button>
          </div>
        </form>
      ) : (
        <p className="text-zinc-550 text-[10px] px-3.5 py-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl">
          Only workspace owners and managers can invite collaborators.
        </p>
      )}

      {/* Pending invitations */}
      <div className="space-y-2.5">
        <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
          Pending Invitations {pendingInvitations.length > 0 && `(${pendingInvitations.length})`}
        </p>

        {invitationsQuery.isLoading ? (
          <InvitationSkeleton />
        ) : invitationsError ? (
          <p className="text-rose-450 text-[10px] font-semibold px-3.5 py-3 bg-rose-500/5 border border-rose-500/10 rounded-2xl">
            {invitationsError}
          </p>
        ) : pendingInvitations.length === 0 ? (
          <p className="text-zinc-550 text-[10px] px-3.5 py-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl">
            No pending invitations.
          </p>
        ) : (
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.uuid}
                className="flex flex-wrap items-center justify-between gap-3 p-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl"
              >
                <div className="min-w-0">
                  <p className="text-white text-xs font-bold truncate">{invitation.email}</p>
                  <p className="text-zinc-550 text-[10px] mt-0.5">
                    {ROLE_LABEL[invitation.role] ?? invitation.role} · Invited{' '}
                    {formatDate(invitation.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[9px] font-bold uppercase px-2.5 py-0.5 rounded-full border bg-amber-500/10 text-amber-400 border-amber-500/20">
                    {invitation.status}
                  </span>
                  {canManage && (
                    <>
                      {/* {!invitation.is_automatic && !invitation.automatic && (
                        <button
                          type="button"
                          title="Resend invitation"
                          disabled={resendMutation.isPending}
                          onClick={() => requestResendInvitation(invitation)}
                          className="p-2 rounded-full border border-blue-500/20 hover:border-blue-500/40 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 transition-all cursor-pointer disabled:opacity-40"
                        >
                          <FiSend className="w-3.5 h-3.5" />
                        </button>
                      )} */}
                      <button
                        type="button"
                        title="Cancel invitation"
                        onClick={() => requestCancelInvitation(invitation)}
                        className="p-2 rounded-full border border-rose-500/20 hover:border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-450 transition-all cursor-pointer"
                      >
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members list */}
      <div className="space-y-2.5">
        <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
          Current Members ({memberCount})
        </p>

        {membersQuery.isLoading ? (
          <MemberSkeleton />
        ) : membersError ? (
          <p className="text-rose-450 text-[10px] font-semibold px-3.5 py-3 bg-rose-500/5 border border-rose-500/10 rounded-2xl">
            {membersError}
          </p>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
            <FiUsers className="w-6 h-6 text-zinc-600" />
            <p className="text-zinc-550 text-xs">No collaborators yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => {
              const self = isSelf(member);
              const isOwner = member.role === 'owner';
              return (
                <div
                  key={member.uuid}
                  className="flex flex-wrap items-center justify-between gap-3 p-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={displayName(member.user)} size="sm" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-white text-xs font-bold truncate">
                          {displayName(member.user)}
                        </span>
                        {self && (
                          <span className="text-zinc-550 text-[9px] font-bold uppercase">You</span>
                        )}
                      </div>
                      <p className="text-zinc-550 text-[10px] mt-0.5 truncate">
                        {member.user.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {canManage && !isOwner && !self ? (
                      <select
                        value={member.role}
                        disabled={updateRoleMutation.isPending}
                        onChange={(e) =>
                          updateRoleMutation.mutate({
                            memberUuid: member.uuid,
                            role: e.target.value as AssignableWorkspaceRole,
                          })
                        }
                        className="bg-zinc-900/60 border border-white/10 text-zinc-300 text-[10px] font-bold uppercase rounded-full px-2.5 py-1 cursor-pointer outline-none"
                      >
                        <option value="editor">Editor</option>
                        <option value="manager">Manager</option>
                      </select>
                    ) : (
                      <span
                        className={`text-[9px] font-bold uppercase px-2.5 py-0.5 rounded-full border ${
                          isOwner
                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                            : 'bg-zinc-800 text-zinc-400 border-white/5'
                        }`}
                      >
                        {ROLE_LABEL[member.role] ?? member.role}
                      </span>
                    )}

                    {!isOwner && (canManage || self) && (
                      <button
                        type="button"
                        title={self ? 'Leave workspace' : 'Remove member'}
                        onClick={() => requestRemoveMember(member)}
                        className="p-2 rounded-full border border-rose-500/20 hover:border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-450 transition-all cursor-pointer"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirm dialog */}
      <AnimatePresence>
        {confirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirm(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="relative w-full max-w-sm bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
            >
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-lg font-bold text-white">{confirm.title}</h2>
                <button
                  onClick={() => setConfirm(null)}
                  className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <p className="text-zinc-400 text-xs mb-6">{confirm.message}</p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirm(null)}
                  className="px-5 py-2.5 rounded-full border border-white/10 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-300 text-xs font-bold transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirm.onConfirm}
                  className={`px-5 py-2.5 rounded-full text-white text-xs font-bold transition-all cursor-pointer ${
                    confirm.isDestructive === false
                      ? 'bg-blue-600 hover:bg-blue-500'
                      : 'bg-rose-500 hover:bg-rose-600'
                  }`}
                >
                  {confirm.confirmLabel}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MemberSkeleton = () => (
  <div className="space-y-2">
    {[0, 1].map((i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl"
      >
        <div className="w-9 h-9 rounded-full bg-white/5 animate-pulse shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-2.5 w-32 rounded bg-white/5 animate-pulse" />
          <div className="h-2 w-40 rounded bg-white/5 animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

const InvitationSkeleton = () => (
  <div className="space-y-2">
    {[0].map((i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-3 bg-[#0F0F0F]/65 border border-white/5 rounded-2xl"
      >
        <div className="flex-1 space-y-1.5">
          <div className="h-2.5 w-40 rounded bg-white/5 animate-pulse" />
          <div className="h-2 w-24 rounded bg-white/5 animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);
